import { useState } from "react";
import { Empty, Tag, Tooltip, Typography, theme } from "antd";
import { HolderOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  containerLabel,
  isContainer,
  isFurniture,
  isTable,
  widgetLabel,
  type Node,
} from "./schema";

const { Text } = Typography;

/** Drop zones are prefixed so a container and its row never share an id. */
const ZONE = "Z#";
const ROOT_ZONE = `${ZONE}__root__`;

type Move = {
  id: string;
  targetParentId: string | null;
  index: number;
};

/* Nodes live at module scope: a component type re-created on each render would
   remount mid-drag and cancel it. */

const FieldRow = ({
  node,
  selected,
  onSelect,
}: {
  node: Node;
  selected: boolean;
  onSelect: () => void;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const sortable = useSortable({ id: node.id });

  return (
    <div
      ref={sortable.setNodeRef}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      style={{
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.4 : 1,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        marginBottom: 4,
        borderRadius: token.borderRadius,
        cursor: "pointer",
        background: token.colorBgContainer,
        border: `1px solid ${
          selected ? token.colorPrimary : token.colorBorderSecondary
        }`,
        boxShadow: selected ? `inset 2px 0 0 ${token.colorPrimary}` : undefined,
      }}
    >
      <span
        {...sortable.attributes}
        {...sortable.listeners}
        onClick={(e) => e.stopPropagation()}
        aria-label={t("Drag to move")}
        style={{
          cursor: "grab",
          color: token.colorTextQuaternary,
          lineHeight: 1,
          touchAction: "none",
        }}
      >
        <HolderOutlined />
      </span>
      <Text ellipsis style={{ flex: 1 }}>
        {node.title || node.key}
      </Text>
      {node.required && (
        <Tooltip title={t("Required")}>
          <Text type="danger">*</Text>
        </Tooltip>
      )}
      <Tag style={{ marginInlineEnd: 0 }}>{t(widgetLabel(node.component))}</Tag>
    </div>
  );
};

const ContainerNode = ({
  node,
  selectedId,
  onSelect,
}: {
  node: Node;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const sortable = useSortable({ id: node.id });
  const drop = useDroppable({ id: `${ZONE}${node.id}` });
  const selected = selectedId === node.id;

  // A repeating table keeps its columns under `items`, not `properties`, and
  // its own "Add row" button is an ordinary child. What an administrator is
  // arranging is the columns; the button is furniture and showing it invites
  // somebody to drag it out, after which no row can be added and nothing on
  // screen says why.
  const contents = isTable(node)
    ? node.itemChildren
    : node.children.filter((child) => !isFurniture(child));

  return (
    <div
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.4 : 1,
        marginBottom: 6,
        borderRadius: token.borderRadius,
        border: `1px solid ${
          selected ? token.colorPrimary : token.colorBorderSecondary
        }`,
        background: token.colorFillQuaternary,
      }}
    >
      <div
        onClick={(e) => {
          e.stopPropagation();
          onSelect(node.id);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 10px",
          cursor: "pointer",
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <span
          {...sortable.attributes}
          {...sortable.listeners}
          onClick={(e) => e.stopPropagation()}
          aria-label={t("Drag to move")}
          style={{
            cursor: "grab",
            color: token.colorTextQuaternary,
            lineHeight: 1,
            touchAction: "none",
          }}
        >
          <HolderOutlined />
        </span>
        <Text type="secondary" style={{ fontSize: 12, flex: 1 }}>
          {containerLabel(node) || t(widgetLabel(node.component))}
        </Text>
        <Tag style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: "14px" }}>
          {t(widgetLabel(node.component))}
        </Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {contents.length}
        </Text>
      </div>

      <div
        ref={drop.setNodeRef}
        style={{
          padding: 8,
          minHeight: 44,
          background: drop.isOver ? token.controlItemBgHover : undefined,
          transition: "background 120ms",
        }}
      >
        <SortableContext
          items={contents.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          {contents.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {isTable(node)
                ? t("No columns yet — add one from the panel on the right.")
                : t("Drop a field here")}
            </Text>
          ) : (
            contents.map((child) =>
              isContainer(child) ? (
                <ContainerNode
                  key={child.id}
                  node={child}
                  selectedId={selectedId}
                  onSelect={onSelect}
                />
              ) : (
                <FieldRow
                  key={child.id}
                  node={child}
                  selected={selectedId === child.id}
                  onSelect={() => onSelect(child.id)}
                />
              )
            )
          )}
        </SortableContext>
      </div>
    </div>
  );
};

type Props = {
  root: Node[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onMove: (move: Move) => void;
  /** Resolve a node id to the node, so the drag overlay can label itself and
   *  drops can be validated against what is being dragged. */
  lookup: (id: string) => Node | null;
  /** Where a node currently lives, for turning a drop onto a sibling into an
   *  index in that sibling's list. */
  parentOf: (id: string) => { parentId: string | null; index: number } | null;
};

/**
 * The form under construction.
 *
 * Containers are drawn as containers rather than as indentation, because the
 * thing that most often goes wrong when building one of these forms is a field
 * landing in the wrong grid column — and that is invisible in a flat list.
 */
const DesignCanvas = ({
  root,
  selectedId,
  onSelect,
  onMove,
  lookup,
  parentOf,
}: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [dragging, setDragging] = useState<UniqueIdentifier | null>(null);
  const rootDrop = useDroppable({ id: ROOT_ZONE });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    const id = String(e.active.id);
    const overId = e.over ? String(e.over.id) : "";
    if (!overId || overId === id) return;

    // Dropped straight onto a container's empty space.
    if (overId.startsWith(ZONE)) {
      const targetParentId =
        overId === ROOT_ZONE ? null : overId.slice(ZONE.length);
      onMove({ id, targetParentId, index: -1 });
      return;
    }

    // Dropped onto another node: land beside it, in its list.
    const at = parentOf(overId);
    if (!at) return;
    const target = lookup(overId);
    // Dropping onto a collapsed container means "inside it", which is what a
    // user expects when the container is the thing under the cursor.
    if (target && isContainer(target) && target.children.length === 0) {
      onMove({ id, targetParentId: target.id, index: -1 });
      return;
    }
    onMove({ id, targetParentId: at.parentId, index: at.index });
  };

  const overlayLabel = () => {
    const node = dragging ? lookup(String(dragging)) : null;
    return node ? node.title || node.key : "";
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e: DragStartEvent) => setDragging(e.active.id)}
      onDragCancel={() => setDragging(null)}
      onDragEnd={onDragEnd}
    >
      <div
        ref={rootDrop.setNodeRef}
        onClick={() => onSelect(null)}
        style={{
          minHeight: 260,
          padding: 10,
          borderRadius: token.borderRadius,
          background: rootDrop.isOver ? token.controlItemBgHover : undefined,
          border: `1px dashed ${token.colorBorderSecondary}`,
        }}
      >
        {root.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("This form is empty. Add a field from the left.")}
          />
        ) : (
          <SortableContext
            items={root.map((n) => n.id)}
            strategy={verticalListSortingStrategy}
          >
            {root.map((node) =>
              isContainer(node) ? (
                <ContainerNode
                  key={node.id}
                  node={node}
                  selectedId={selectedId}
                  onSelect={onSelect}
                />
              ) : (
                <FieldRow
                  key={node.id}
                  node={node}
                  selected={selectedId === node.id}
                  onSelect={() => onSelect(node.id)}
                />
              )
            )}
          </SortableContext>
        )}
      </div>

      <DragOverlay>
        {dragging ? (
          <div
            style={{
              padding: "6px 12px",
              borderRadius: token.borderRadius,
              background: token.colorBgElevated,
              boxShadow: token.boxShadowSecondary,
            }}
          >
            {overlayLabel()}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default DesignCanvas;
